import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import type { LocalizedText } from '../../../lib/localizedText'
import { InputSwitch } from 'primereact/inputswitch'
import Button from '../../../components/ui/Button'
import Badge from '../../../components/ui/Badge'
import SortSpinner from '../../../components/ui/SortSpinner'
import type { Stakeholder } from '../types'

interface ColumnOpts {
  t: TFunction
  /** Ad kolonu tek kolondur; değer seçili dilden gelir (`lib/localizedText`). */
  localized: LocalizedText
  busyId: number | null
  /** Görünen satır sayısı — son satırda "aşağı" pasif olması için. */
  rowCount: number
  /** Yeniden sıralama sürüyor mu — oklar kilitlenir. */
  reordering: boolean
  onMove: (row: Stakeholder, dir: 'up' | 'down') => void
  onEdit: (row: Stakeholder) => void
  onToggle: (row: Stakeholder, active: boolean) => void
  onDelete: (row: Stakeholder) => void
}

/**
 * Paydaş/katılımcı tablosunun sütunları (paylaşılan ui/DataTable'a beslenir). Baştaki **sıra** kolonu yan
 * yana ↑/↓ okları (ortak SortSpinner) — sıra numarası gösterilmez, taşıma anında kalıcı. Durum satır-içi
 * InputSwitch (Aktif/Pasif) + satır aksiyonları (düzenle/sil). **Korumalı sistem kaydı yoktur** (012'den fark).
 * (012 RejectReasonColumns deseniyle aynı.)
 */
export function stakeholderColumns({
  t,
  localized,
  busyId,
  rowCount,
  reordering,
  onMove,
  onEdit,
  onToggle,
  onDelete,
}: ColumnOpts): ReactElement[] {
  return [
    // Sıra kolonu — yalnızca oklar (SORT_INDEX değeri gösterilmez); liste daima sıra düzeninde.
    <Column
      key="sort"
      header={t('activity.stakeholders.columns.sortIndex')}
      style={{ width: '6rem' }}
      body={(row: Stakeholder, { rowIndex }) => (
        <SortSpinner
          onUp={() => onMove(row, 'up')}
          onDown={() => onMove(row, 'down')}
          disableUp={rowIndex === 0}
          disableDown={rowIndex === rowCount - 1}
          busy={reordering}
          labelUp={t('common.reorder.up')}
          labelDown={t('common.reorder.down')}
        />
      )}
    />,
    <Column
      key="name"
      header={t('activity.stakeholders.columns.name')}
      body={(row: Stakeholder) => localized(row.nameTr, row.nameEn)}
    />,
    <Column
      key="status"
      header={t('activity.stakeholders.columns.status')}
      style={{ width: '8rem' }}
      body={(row: Stakeholder) => {
        const active = row.recordStatus === 1
        return (
          <Badge tone={active ? 'green' : 'neutral'}>
            {active
              ? t('activity.stakeholders.status.active')
              : t('activity.stakeholders.status.passive')}
          </Badge>
        )
      }}
    />,
    <Column
      key="actions"
      header={t('activity.stakeholders.columns.actions')}
      style={{ width: '10rem' }}
      body={(row: Stakeholder) => {
        const active = row.recordStatus === 1
        return (
          <div className="admin-row-actions">
            <InputSwitch
              className="admin-switch"
              checked={active}
              disabled={busyId === row.id}
              onChange={(e) => onToggle(row, Boolean(e.value))}
              aria-label={row.nameTr}
              tooltip={t('activity.stakeholders.actions.toggle')}
              tooltipOptions={{ position: 'bottom' }}
            />
            <Button
              icon="pi pi-pencil"
              rounded
              text
              aria-label={t('activity.stakeholders.actions.edit')}
              tooltip={t('activity.stakeholders.actions.edit')}
              tooltipOptions={{ position: 'bottom' }}
              onClick={() => onEdit(row)}
            />
            <Button
              icon="pi pi-trash"
              rounded
              text
              severity="danger"
              aria-label={t('activity.stakeholders.actions.delete')}
              tooltip={t('activity.stakeholders.actions.delete')}
              tooltipOptions={{ position: 'bottom' }}
              disabled={busyId === row.id}
              onClick={() => onDelete(row)}
            />
          </div>
        )
      }}
    />,
  ]
}
